import { MarketStatus, TradeStatus } from '@sportspredict/shared';

/** Open markets the user has not traded yet. */
export const getOpenUntradedMarketIds = (markets: any[], userTrades: any[] | undefined) =>
	markets
		.filter(
			(m: any) =>
				m.status === MarketStatus.Open && !userTrades?.some((t: any) => t.market_id === m.id)
		)
		.map((m: any) => m.id);

/** The slates each match belongs to. */
export const getMatchToSlateIds = (slates: any[] | undefined) => {
	const map = new Map<string, string[]>();
	(slates || []).forEach((slate: any) => {
		(slate.matches || []).forEach((m: any) => {
			const arr = map.get(m.id) || [];
			arr.push(slate.id);
			map.set(m.id, arr);
		});
	});
	return map;
};

/** The user's trades (and wins) per slate. */
export const getSlateUserTradeCounts = (
	markets: any[] | undefined,
	userTrades: any[] | undefined,
	matchToSlateIds: Map<string, string[]>
) => {
	const counts: Record<string, { total: number; wins: number }> = {};
	if (!markets || !userTrades) return counts;
	userTrades.forEach((t: any) => {
		const market = markets.find((m: any) => m.id === t.market_id);
		if (!market) return;
		const slateIds = matchToSlateIds.get(market.match_id) || [];
		slateIds.forEach((sid) => {
			if (!counts[sid]) counts[sid] = { total: 0, wins: 0 };
			counts[sid].total += 1;
			if (t.status === TradeStatus.Win) counts[sid].wins += 1;
		});
	});
	return counts;
};

/** Open, untraded markets grouped by the slate of their match. */
export const getSlateAvailableMarkets = (
	markets: any[] | undefined,
	slates: any[] | undefined,
	userTrades: any[] | undefined
) => {
	const result: Record<string, { name: any; count: number; markets: any[] }> = {};

	if (!markets?.length || !slates?.length) return result;

	markets.forEach((market: any) => {
		if (market.status !== MarketStatus.Open || !market.match_id) return;

		const hasUserTrade = userTrades?.some((t: any) => t.market_id === market.id);
		if (hasUserTrade) return;

		const slate = slates.find((sl: any) => sl.matches?.some((m: any) => m.id === market.match_id));

		if (!slate) return;

		if (!result[slate.id]) {
			result[slate.id] = {
				name: slate.name,
				count: 1,
				markets: [market.id],
			};
		} else {
			result[slate.id].count++;
			result[slate.id].markets.push(market.id);
		}
	});

	return result;
};

/** Markets of the slates where the user is still under the slate trade limit. */
export const getAvailableSlateMarketIds = (
	slateAvailableMarkets: Record<string, { markets: any[] }>,
	slateTradeLimit: number | null | undefined,
	slateUserTradeCounts: Record<string, { total: number }> | undefined
) => {
	const available: any[] = [];
	if (!slateTradeLimit) return available;

	Object.entries(slateAvailableMarkets).forEach(([id, slData]) => {
		const made = slateUserTradeCounts?.[String(id)]?.total ?? 0;

		if (made < slateTradeLimit) {
			available.push(...slData.markets);
		}
	});

	return available;
};

/**
 * The lobby header's "Open Markets" number: open markets the user has not traded or,
 * in slate-limited lobbies, the "Available Markets" in slates the user can still trade.
 */
export const getLobbyOpenMarketsCount = ({
	markets,
	userTrades,
	slates,
	slateTradeLimit,
}: {
	markets: any[];
	userTrades: any[] | undefined;
	slates: any[] | undefined;
	slateTradeLimit: number | null | undefined;
}) => {
	if (!slateTradeLimit) return getOpenUntradedMarketIds(markets, userTrades).length;
	const slateUserTradeCounts = getSlateUserTradeCounts(
		markets,
		userTrades,
		getMatchToSlateIds(slates)
	);
	return getAvailableSlateMarketIds(
		getSlateAvailableMarkets(markets, slates, userTrades),
		slateTradeLimit,
		slateUserTradeCounts
	).length;
};
